import { useCallback, useEffect, useMemo, useRef } from "react"
import {
  DEFAULT_LIST_PAGE_SIZE,
  getListPageRange,
  isListPageSize,
  ListPageSize,
  pageAfterPageSizeChange,
} from "./listPagination"

export interface UseClientListPaginationArgs {
  page: number
  pageSize: ListPageSize
  setPage: React.Dispatch<React.SetStateAction<number>>
  setPageSize: React.Dispatch<React.SetStateAction<ListPageSize>>
  /** Search / filter / sort identity. When this string changes, page resets to 1. */
  queryKey: string
}

export function useClientListPagination<T>(
  rows: T[],
  { page, pageSize, setPage, setPageSize, queryKey }: UseClientListPaginationArgs,
) {
  const prevQueryKey = useRef(queryKey)

  useEffect(() => {
    if (prevQueryKey.current === queryKey) return
    prevQueryKey.current = queryKey
    setPage(1)
  }, [queryKey, setPage])

  const range = useMemo(
    () => getListPageRange(rows.length, page, pageSize),
    [rows.length, page, pageSize],
  )

  useEffect(() => {
    if (range.pageCount > 0 && page > range.pageCount) {
      setPage(range.pageCount)
    }
  }, [page, range.pageCount, setPage])

  const pagedRows = useMemo(
    () => rows.slice(range.sliceStart, range.sliceEnd),
    [rows, range.sliceStart, range.sliceEnd],
  )

  const handlePageChange = useCallback(
    (nextPage: number) => {
      if (range.pageCount <= 0) return
      const clamped = Math.min(Math.max(nextPage, 1), range.pageCount)
      setPage(clamped)
    },
    [range.pageCount, setPage],
  )

  const handlePageSizeChange = useCallback(
    (nextSize: number) => {
      if (!isListPageSize(nextSize)) return
      const nextPage = pageAfterPageSizeChange(page, rows.length, nextSize)
      setPageSize(nextSize)
      if (nextPage !== page) setPage(nextPage)
    },
    [page, rows.length, setPage, setPageSize],
  )

  return {
    pagedRows,
    total: rows.length,
    page: range.pageCount > 0 ? Math.min(page, range.pageCount) : page,
    pageSize: pageSize || DEFAULT_LIST_PAGE_SIZE,
    pageCount: range.pageCount,
    from: range.from,
    to: range.to,
    handlePageChange,
    handlePageSizeChange,
  }
}
